import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    CheckmarkCircle12Filled,
    Warning12Filled,
    DismissCircle12Filled,
    Dismiss12Regular,
    MoreHorizontal16Regular,
} from '@fluentui/react-icons';
import {
    MODULE_CATEGORY_ORDER,
    OVERVIEW_VARIANTS,
} from './overviewDummyData';
import {
    AnalysisDetailsDrawer,
    GenerateVersionModal,
    ModuleConfigModal,
    OverviewActionMenu,
    SourceReadinessDrawer,
    SourceSnapshotDrawer,
    VersionHistoryDrawer,
} from './OverviewOverlays';
import styles from './intelligenceInsightsOverview.module.css';

function titleCaseTone(tone) {
    const t = String(tone || 'amber').toLowerCase();
    if (t === 'green') return 'Green';
    if (t === 'red') return 'Red';
    if (t === 'grey' || t === 'gray') return 'Grey';
    return 'Amber';
}

const StatusBadge = ({ status, label }) => {
    if (status === 'warnings') {
        return (
            <span className={styles.statusWarnings}>
                <Warning12Filled className={styles.statusIconLg} aria-hidden />
                {label || 'Complete with warnings'}
            </span>
        );
    }
    if (status === 'new_evidence') {
        return (
            <span className={styles.statusNewEvidence}>
                <span className={styles.statusDot} aria-hidden />
                {label || 'New evidence available'}
            </span>
        );
    }
    if (status === 'pending') {
        return (
            <span className={styles.statusPending}>{label || 'Pending'}</span>
        );
    }
    return (
        <span className={styles.statusComplete}>
            <CheckmarkCircle12Filled className={styles.statusIcon} aria-hidden />
            {label || 'Complete'}
        </span>
    );
};

const OverviewBanner = ({ type, data, onDismiss, onAction }) => {
    if (!data) return null;
    const isWarning = type === 'warning';

    return (
        <div
            className={`${styles.banner} ${isWarning ? styles.bannerWarning : styles.bannerInfo}`}
            role="status">
            <div className={styles.bannerTop}>
                {isWarning ? (
                    <Warning12Filled className={styles.bannerIcon} aria-hidden />
                ) : (
                    <span className={styles.bannerInfoDot} aria-hidden />
                )}
                <p className={styles.bannerTitle}>{data.title}</p>
                <button
                    type="button"
                    className={styles.bannerDismiss}
                    onClick={onDismiss}
                    aria-label="Dismiss">
                    <Dismiss12Regular />
                </button>
            </div>
            {data.message ? <p className={styles.bannerMessage}>{data.message}</p> : null}
            {data.source ? <p className={styles.bannerSource}>{data.source}</p> : null}
            {data.affected ? <p className={styles.bannerAffected}>{data.affected}</p> : null}
            {data.note ? <p className={styles.bannerNote}>{data.note}</p> : null}
            {data.actions?.length ? (
                <div className={styles.bannerActions}>
                    {data.actions.map(action => (
                        <button
                            key={action.key}
                            type="button"
                            className={styles.btnGhostSm}
                            onClick={() => onAction?.(action.key)}>
                            {action.label}
                        </button>
                    ))}
                </div>
            ) : null}
        </div>
    );
};

const GenerateCtaCard = ({ data, onGenerate, generating, onOpenChangeContext }) => {
    if (!data) return null;
    return (
        <div className={`${styles.card} ${styles.cardPad}`}>
            <h2 className={styles.generateCardTitle}>{data.title}</h2>
            <p className={styles.generateCardDesc}>{data.description}</p>
            <div className={styles.generateRows}>
                {data.rows.map(row => (
                    <div key={row.label} className={styles.generateRow}>
                        <span className={styles.generateRowLabel}>{row.label}</span>
                        <span className={styles.generateRowValue}>{row.value}</span>
                    </div>
                ))}
            </div>
            <p className={styles.generateNote}>{data.note}</p>
            <div className={styles.generateActions}>
                <button
                    type="button"
                    className={styles.btnSecondary}
                    onClick={() => onOpenChangeContext?.()}>
                    Change context
                </button>
                <button
                    type="button"
                    className={styles.btnPrimary}
                    onClick={onGenerate}
                    disabled={generating}>
                    {generating ? 'Generating…' : 'Generate analysis →'}
                </button>
            </div>
        </div>
    );
};

const NoReadySourcesCard = ({ data, onReviewSources }) => {
    if (!data) return null;
    return (
        <div className={`${styles.card} ${styles.cardPad}`}>
            <div className={styles.unavailableHeader}>
                <p className={styles.unavailableEyebrow}>{data.title}</p>
                <span className={styles.unavailableStatus}>
                    <DismissCircle12Filled className={styles.unavailableIcon} aria-hidden />
                    {data.status}
                </span>
            </div>
            <p className={styles.unavailableMessage}>{data.message}</p>
            <p className={styles.unavailableReadiness}>{data.readiness}</p>
            <p className={styles.unavailableNote}>{data.note}</p>
            <button type="button" className={styles.btnSecondary} onClick={onReviewSources}>
                {data.actionLabel}
            </button>
        </div>
    );
};

const CurrentAnalysisCard = ({ data, onOpenDetails, onOpenModule }) => {
    if (!data?.hasAnalysis) return null;
    const statusKey = data.analysisStatus?.key;

    return (
        <div className={`${styles.card} ${styles.cardPadTight}`}>
            <div className={styles.analysisCard}>
                <div className={styles.analysisMain}>
                    <div className={styles.analysisLabelRow}>
                        <p className={styles.analysisEyebrow}>Current analysis</p>
                        <StatusBadge status={statusKey} label={data.analysisStatus?.label} />
                    </div>
                    <p className={styles.analysisVersion}>{data.versionLabel}</p>
                    <p className={styles.analysisModulesLine}>
                        {data.modulesAvailable} insight modules available
                    </p>
                    <p className={styles.analysisConfidence}>{data.confidence}</p>
                    <div className={styles.analysisLinks}>
                        <button
                            type="button"
                            className={styles.linkPrimary}
                            onClick={() => onOpenModule?.('management_summary')}>
                            Open Management Summary →
                        </button>
                        <button type="button" className={styles.linkMuted} onClick={onOpenDetails}>
                            View details
                        </button>
                    </div>
                </div>
                <div className={styles.analysisStats}>
                    <div className={styles.statBlock}>
                        <p className={styles.statValue}>{data.modulesStat.value}</p>
                        <p className={styles.statLabel}>{data.modulesStat.label}</p>
                        <p className={styles.statSublabel}>{data.modulesStat.sublabel}</p>
                    </div>
                    <div className={styles.statDivider} aria-hidden />
                    <div className={styles.statBlock}>
                        <p className={styles.statValue}>{data.readyStat.value}</p>
                        <p className={styles.statLabel}>{data.readyStat.label}</p>
                        <p className={styles.statSublabel}>{data.readyStat.sublabel}</p>
                    </div>
                    <div className={styles.statDivider} aria-hidden />
                    <div className={styles.statBlock}>
                        <p className={styles.statValue}>{data.warningStat.value}</p>
                        <p className={styles.statLabel}>{data.warningStat.label}</p>
                    </div>
                </div>
            </div>
        </div>
    );
};

const ContextAndReadiness = ({ data, onReviewSources }) => {
    if (!data?.hasAnalysis) return null;
    const { analysisContext, sourceReadiness } = data;

    return (
        <div className={styles.midGrid}>
            <div className={`${styles.card} ${styles.cardPad}`}>
                <p className={styles.sectionEyebrow}>Analysis context</p>
                <p className={styles.contextTitle}>{analysisContext.title}</p>
                <p className={styles.contextDesc}>{analysisContext.description}</p>
                <p className={styles.contextBreakdown}>{analysisContext.breakdown}</p>
            </div>
            <div className={`${styles.card} ${styles.cardPad}`}>
                <p className={styles.sectionEyebrow}>Source readiness</p>
                <div className={styles.readinessList}>
                    <div className={styles.readinessRow}>
                        <span className={styles.readyDot} aria-hidden />
                        {sourceReadiness.ready} Ready
                    </div>
                    <div className={styles.readinessRow}>
                        <span className={styles.processingDot} aria-hidden />
                        {sourceReadiness.processing} Processing
                    </div>
                    <div className={styles.readinessRow}>
                        <DismissCircle12Filled className={styles.failedIcon} aria-hidden />
                        {sourceReadiness.failed} Failed
                    </div>
                </div>
                <p className={styles.readinessNote}>{sourceReadiness.note}</p>
                <button type="button" className={styles.reviewSourcesLink} onClick={onReviewSources}>
                    Review sources →
                </button>
            </div>
        </div>
    );
};

const ModuleCard = ({ module, onOpen, onOpenDetails, onGenerateNewVersion }) => {
    const openLabel = module.openLabel || 'Open →';

    return (
        <article className={styles.moduleCard}>
            <div className={styles.moduleCardHeader}>
                <h3 className={styles.moduleCardTitle}>{module.title}</h3>
                <StatusBadge
                    status={module.status}
                    label={
                        module.status === 'warnings'
                            ? 'Complete with warnings'
                            : module.status === 'new_evidence'
                              ? 'New evidence available'
                              : 'Complete'
                    }
                />
            </div>
            <p className={styles.moduleCardDesc}>{module.description}</p>
            {module.metricPrefix && module.ragPill ? (
                <div className={styles.moduleCardMetricRow}>
                    <span className={styles.moduleCardMetric}>{module.metricPrefix}</span>
                    <span
                        className={`${styles.ragPill} ${
                            styles[
                                `ragPill${titleCaseTone(module.ragPill.tone)}`
                            ] || styles.ragPillAmber
                        }`}>
                        <span className={styles.ragPillDot} aria-hidden />
                        <span className={styles.ragPillLabel}>{module.ragPill.label}</span>
                    </span>
                </div>
            ) : null}
            {module.posturePill ? (
                <span
                    className={`${styles.posturePill} ${
                        styles[
                            `posturePill${titleCaseTone(module.posturePill.tone)}`
                        ] || ''
                    }`}>
                    {module.posturePill.label}
                </span>
            ) : null}
            {typeof module.metric === 'string' || typeof module.metric === 'number' ? (
                <p className={styles.moduleCardMetric}>{module.metric}</p>
            ) : null}
            {module.confidence ? (
                <p className={styles.moduleCardConfidence}>{module.confidence}</p>
            ) : null}
            {module.warningNote ? (
                <div className={styles.moduleWarning}>
                    <Warning12Filled className={styles.moduleWarningIcon} aria-hidden />
                    {module.warningNote}
                </div>
            ) : null}
            <div className={styles.moduleCardFooter}>
                <button type="button" className={styles.openLink} onClick={() => onOpen?.(module.key)}>
                    {openLabel}
                </button>
                {module.status === 'warnings' ? (
                    <button
                        type="button"
                        className={styles.openLinkMuted}
                        onClick={() => onOpenDetails?.()}>
                        Review warning
                    </button>
                ) : null}
                {module.secondaryActionLabel ? (
                    <button
                        type="button"
                        className={styles.openLinkMuted}
                        onClick={() => onGenerateNewVersion?.(module.key)}>
                        {module.secondaryActionLabel}
                    </button>
                ) : null}
            </div>
        </article>
    );
};

const ModuleSections = ({ modules, onOpen, onOpenDetails, onGenerateNewVersion }) => {
    if (!modules?.length) return null;

    return (
        <div className={styles.moduleSections}>
            {MODULE_CATEGORY_ORDER.map(section => {
                const items = modules.filter(m => m.category === section.key);
                if (!items.length) return null;
                const gridClass =
                    section.columns === 3 ? styles.moduleGridCols3 : styles.moduleGridCols2;
                return (
                    <section key={section.key}>
                        <h2 className={styles.moduleSectionTitle}>{section.label}</h2>
                        <div className={`${styles.moduleGrid} ${gridClass}`}>
                            {items.map(mod => (
                                <ModuleCard
                                    key={mod.key}
                                    module={mod}
                                    onOpen={onOpen}
                                    onOpenDetails={onOpenDetails}
                                    onGenerateNewVersion={onGenerateNewVersion}
                                />
                            ))}
                        </div>
                    </section>
                );
            })}
        </div>
    );
};

/**
 * Intelligence Insights Overview — live V2 overview (optional demo selector).
 */
const IntelligenceInsightsOverview = ({
    data,
    variant,
    onVariantChange,
    demoOptions = null,
    onOpenModule,
    onGenerate,
    onBannerAction,
    onOpenChangeContext,
    onOpenVersion,
    sourceReadinessFocus = null,
    onSourceReadinessFocusConsumed,
    loading = false,
    generating = false,
}) => {
    const [bannerDismissed, setBannerDismissed] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [drawer, setDrawer] = useState(null); // versions | details | sources
    const [sourceReadinessDrawerFocus, setSourceReadinessDrawerFocus] = useState(null);
    const [showConfig, setShowConfig] = useState(false);
    const [showGenerateModal, setShowGenerateModal] = useState(false);
    const moreRef = useRef(null);

    useEffect(() => {
        if (!sourceReadinessFocus) return;
        setSourceReadinessDrawerFocus({
            tab: sourceReadinessFocus.tab || 'Failed',
            sourceId: sourceReadinessFocus.sourceId || null,
        });
        setDrawer('sources');
        onSourceReadinessFocusConsumed?.();
    }, [sourceReadinessFocus, onSourceReadinessFocusConsumed]);

    const showGenerated = data.hasAnalysis;
    const showGenerateCta = data.variant === OVERVIEW_VARIANTS.NOT_GENERATED;
    const showNoReady = data.variant === OVERVIEW_VARIANTS.NO_READY_SOURCES;
    const showGenerateAction = !showNoReady;
    // Single banner rule (brief precedence). Prefer warningBanner over newEvidenceBanner.
    const activeBanner = data.warningBanner
        ? { type: 'warning', data: data.warningBanner }
        : data.newEvidenceBanner
          ? { type: 'info', data: data.newEvidenceBanner }
          : null;

    const closeOverlays = () => {
        setDrawer(null);
        setSourceReadinessDrawerFocus(null);
        setShowConfig(false);
        setShowGenerateModal(false);
        setMenuOpen(false);
    };

    const handlePrimaryGenerate = () => {
        setMenuOpen(false);
        if (showNoReady) return;
        if (showGenerated) {
            setShowGenerateModal(true);
            return;
        }
        onGenerate?.();
    };

    const headerMenuItems = useMemo(() => {
        const items = [];
        if (showGenerated) {
            items.push({ key: 'versions', label: 'View versions' });
            items.push({ key: 'details', label: 'View analysis details' });
            if (data?.secondaryViews?.moduleConfig?.length) {
                items.push({ key: 'divider' });
                items.push({ key: 'config', label: 'Module configuration' });
            }
        }
        return items;
    }, [showGenerated, data?.secondaryViews?.moduleConfig]);

    const handleMenuSelect = key => {
        if (key === 'versions') setDrawer('versions');
        if (key === 'details') setDrawer('details');
        if (key === 'config') setShowConfig(true);
    };

    const handleBannerAction = actionKey => {
        if (actionKey === 'review_failed') {
            setSourceReadinessDrawerFocus({ tab: 'Failed', sourceId: null });
            setDrawer('sources');
            return;
        }
        if (actionKey === 'review_evidence' || actionKey === 'review_sources') {
            setSourceReadinessDrawerFocus({ tab: 'All', sourceId: null });
            setDrawer('sources');
            return;
        }
        if (actionKey === 'generate_v4' || actionKey === 'generate_new_version') {
            setShowGenerateModal(true);
            return;
        }
        onBannerAction?.(actionKey);
    };

    return (
        <div className={styles.overviewRoot}>
            <header className={styles.overviewHeader}>
                <div className={styles.overviewHeaderTop}>
                    <h1 className={styles.overviewTitle}>Intelligence Insights</h1>
                    <div className={styles.overviewHeaderActions}>
                        {onOpenChangeContext ? (
                            <button
                                type="button"
                                className={styles.btnSecondary}
                                onClick={() => onOpenChangeContext()}>
                                Change context
                            </button>
                        ) : null}
                        {showGenerateAction ? (
                            <button
                                type="button"
                                className={styles.btnPrimary}
                                onClick={handlePrimaryGenerate}
                                disabled={generating}>
                                {generating
                                    ? 'Generating…'
                                    : data.primaryActionLabel || 'Generate analysis'}
                            </button>
                        ) : null}
                        {headerMenuItems.length ? (
                            <div className={styles.splitWrap} ref={moreRef}>
                                <button
                                    type="button"
                                    className={styles.btnIcon}
                                    aria-label="More actions"
                                    aria-expanded={menuOpen}
                                    aria-haspopup="menu"
                                    onClick={() => setMenuOpen(v => !v)}>
                                    <MoreHorizontal16Regular />
                                </button>
                                <OverviewActionMenu
                                    open={menuOpen}
                                    anchorRef={moreRef}
                                    items={headerMenuItems}
                                    onClose={() => setMenuOpen(false)}
                                    onSelect={handleMenuSelect}
                                />
                            </div>
                        ) : null}
                    </div>
                </div>
                <p className={styles.overviewSubtitle}>
                    {data.subtitle ||
                        'Evidence-backed technology analysis across reporting, strategic assessment and validation.'}
                </p>
                <p className={styles.overviewMeta}>
                    {loading ? 'Loading analysis…' : data.metaLine}
                </p>
                {onVariantChange && demoOptions?.length ? (
                    <div className={styles.demoBar}>
                        <span className={styles.demoLabel}>Demo state</span>
                        <select
                            className={styles.demoSelect}
                            value={variant}
                            onChange={e => {
                                setBannerDismissed(false);
                                onVariantChange(e.target.value);
                            }}
                            aria-label="Overview demo state">
                            {demoOptions.map(opt => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </option>
                            ))}
                        </select>
                    </div>
                ) : null}
            </header>

            <div className={styles.overviewBody}>
                <div className={styles.overviewInner}>
                    {showNoReady ? (
                        <NoReadySourcesCard
                            data={data.noReadySources}
                            onReviewSources={() => setDrawer('sources')}
                        />
                    ) : null}
                    {showGenerateCta ? (
                        <GenerateCtaCard
                            data={data.generateCta}
                            onGenerate={onGenerate}
                            generating={generating}
                            onOpenChangeContext={onOpenChangeContext}
                        />
                    ) : null}

                    {showGenerated ? (
                        <>
                            {!bannerDismissed && activeBanner ? (
                                <OverviewBanner
                                    type={activeBanner.type}
                                    data={activeBanner.data}
                                    onDismiss={() => setBannerDismissed(true)}
                                    onAction={handleBannerAction}
                                />
                            ) : null}
                            <CurrentAnalysisCard
                                data={data}
                                onOpenDetails={() => setDrawer('details')}
                                onOpenModule={onOpenModule}
                            />
                            <ContextAndReadiness
                                data={data}
                                onReviewSources={() => setDrawer('sources')}
                            />
                            <ModuleSections
                                modules={data.modules}
                                onOpen={onOpenModule}
                                onOpenDetails={() => setDrawer('details')}
                                onGenerateNewVersion={() => setShowGenerateModal(true)}
                            />
                        </>
                    ) : null}
                </div>
            </div>

            <VersionHistoryDrawer
                open={drawer === 'versions'}
                onClose={closeOverlays}
                versions={data?.secondaryViews?.versions || []}
                onOpenVersion={version => {
                    closeOverlays();
                    onOpenVersion?.(version);
                }}
            />
            <AnalysisDetailsDrawer
                open={drawer === 'details'}
                onClose={closeOverlays}
                onOpenVersions={() => setDrawer('versions')}
                onOpenSourceSnapshot={
                    data?.secondaryViews?.sourceSnapshot
                        ? () => setDrawer('source_snapshot')
                        : undefined
                }
                rows={data?.secondaryViews?.analysisDetails || []}
            />
            <SourceSnapshotDrawer
                open={drawer === 'source_snapshot'}
                onClose={closeOverlays}
                data={data?.secondaryViews?.sourceSnapshot}
            />
            <SourceReadinessDrawer
                open={drawer === 'sources'}
                onClose={closeOverlays}
                data={data?.secondaryViews?.sourceReadiness}
                initialTab={sourceReadinessDrawerFocus?.tab}
                highlightSourceId={sourceReadinessDrawerFocus?.sourceId}
            />
            <ModuleConfigModal
                open={showConfig}
                onClose={() => setShowConfig(false)}
                modules={data?.secondaryViews?.moduleConfig || []}
            />
            <GenerateVersionModal
                open={showGenerateModal}
                generating={generating}
                data={data?.secondaryViews?.generateModal}
                onClose={() => setShowGenerateModal(false)}
                onConfirm={async () => {
                    setShowGenerateModal(false);
                    await onGenerate?.();
                }}
            />
        </div>
    );
};

export default IntelligenceInsightsOverview;
